import { useEffect, useState } from 'react'
import AppleSignin from 'react-apple-signin-auth'
import { useCookies } from 'react-cookie'
import { SubmitHandler, useForm } from 'react-hook-form'
import KakaoLogin from 'react-kakao-login'
import NaverLogin from 'react-naver-login'
import {
  GTM_CLICK_EVENT,
  getEventUserType,
  sendGTMBasicPageViewEvent,
  sendGTMCustomClickEvent,
} from '../../../helpers/gtmHelper'
import {
  SOCIAL_LOGIN_PROVIDER,
  emailRegExpServer,
  noWhitespaceRegExp,
  processSNS,
  useLogin,
} from '../../../modules/auth'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { RINGLE_DOMAIN } from '../../../modules/envVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../modules/hooks/common/useIsMobile'
import useAppleLogin from '../../../modules/hooks/student/auth/useAppleLogin'
import useKakaoLogin from '../../../modules/hooks/student/auth/useKakaoLogin'
import LinkLocale from '../../../modules/i18n/LinkLocale'
import { s_signin } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { DATA_REF_KEY } from '../../../modules/ids/e2e/student'
import { STUDENT_BTN_ID, STUDENT_PAGE_VIEW_ID } from '../../../modules/ids/gtm/student'
import urls from '../../../modules/urls'
import { detectInAppBrowser } from '../../../modules/utils'
import { COOKIE_LIST, COOKIE_OPT, FACEBOOK_APP_ID, KAKAO_KEY, NAVER_KEY, SESSION_ROLE } from '../../../modules/vars'
import Button from '../../_legacy/Button'
import Input from '../../_legacy/Input'
import CenterContainer from '../../common/CenterContainer'
import DeviceSplit from '../../common/DeviceSplit'
import SignUpLogoNew from '../../common/SignUpLogoNew'
import LandingNav from '../../landing/navigation/LandingNav'
import MobileLandingNav from '../../landing/navigation/LandingNav/MobileLandingNav'
import FacebookLoginBtn from '../FacebookLoginBtn'
import GoogleLogin from '../GoogleLogin'

declare global {
  interface Window {
    gapi: any
  }
}

interface ILoginFormInputs {
  email: string
  password: string
}

export const SignInPageNew = ({ ogData, isMobileServer }) => {
  const { t, locale } = useTranslation()
  const { currentUser } = useCurrentUser()

  const router = useAppRouter()
  const [isInApp, setIsInApp] = useState(false)
  const [cookies, setCookies] = useCookies([COOKIE_LIST.LOGIN_AFTER_URL])

  const isMobile = useIsMobile(isMobileServer)

  // --------소셜로그인---------
  const [loadNaver, setLoadNaver] = useState(false)
  const handleKakaoLogin = useKakaoLogin(locale, isMobile)
  const responseApple = useAppleLogin(locale, isMobile)
  const space = 'mx-6'
  // ------------------------

  // --------로그인 폼---------
  const login = useLogin()
  const [isLoginLoading, setIsLoginLoading] = useState<boolean>(false)
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isValid, errors },
  } = useForm<ILoginFormInputs>({ mode: 'onTouched' })

  const handleLogin: SubmitHandler<ILoginFormInputs> = async (data) => {
    if (isLoginLoading) return

    const email = data.email
    const password = data.password

    setIsLoginLoading(true)
    await login(email, password, SESSION_ROLE.STUDENT, locale, isMobile)
    setIsLoginLoading(false)
  }

  const handleKakaoInapp = () => {
    if (window?.Kakao) {
      if (!window?.Kakao?.isInitialized()) window.Kakao.init(KAKAO_KEY)
      window.Kakao?.Auth.authorize({
        redirectUri: `https://${RINGLE_DOMAIN}/${locale}${urls.student.auth.signUpSocialCallback}?type=kakao`,
      })
    } else {
      console.error('window.Kakao undefined')
    }
  }

  useEffect(() => {
    const isInAppBrowser = isMobile && detectInAppBrowser(navigator.userAgent || navigator.vendor || window['opera'])
    setIsInApp(isInAppBrowser)

    setLoadNaver(true)
    window['naverLogin'] = (data) => {
      processSNS(router, data, isMobile, null, SOCIAL_LOGIN_PROVIDER.NAVER)
    }

    if (isInAppBrowser) {
      if (!window) return
      if (window.Kakao) {
        if (!window.Kakao.isInitialized()) window.Kakao.init(KAKAO_KEY)
      } else {
        console.error('window.Kakao undefined')
      }
    }
  }, [])
  // ------------------------

  useEffect(() => {
    if (router.query?.redirect_url) {
      if (currentUser) {
        router.push(router.query.redirect_url as string)
      } else {
        setCookies(COOKIE_LIST.LOGIN_AFTER_URL, router.query.redirect_url as string, COOKIE_OPT)
      }
    }
  }, [router.query])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const onClickSignUp = () => {
    sendGTMCustomClickEvent(
      GTM_CLICK_EVENT.CLICK_BUTTON_NEW,
      STUDENT_BTN_ID.SIGN_UP,
      '회원가입',
      getEventUserType(currentUser),
      'Sign In',
      router.asPath
    )
  }

  useEffect(() => {
    sendGTMBasicPageViewEvent(
      STUDENT_PAGE_VIEW_ID.AUTH_SIGN_IN,
      getEventUserType(null),
      locale,
      'Sign In',
      router.asPath
    )
  }, [])

  return (
    <div>
      <DeviceSplit isMobile={isMobile} mobile={<MobileLandingNav />} desktop={<LandingNav />} />
      <CenterContainer clx="pb-[52px] pt-[28px] sm:py-[92px]">
        <div className="mx-auto w-full max-w-[405px] text-center">
          {/* Log in to Ringle */}
          <div className="sm:text-d4 text-h1 mb-[32px] font-bold">{t(s_signin.title)}</div>

          {/* 로그인 폼 */}
          <form noValidate onSubmit={handleSubmit(handleLogin)} method="post">
            {/* 이메일 입력란 */}
            <div className="mb-[8px]">
              <Input
                tabIndex={1}
                dataRef={DATA_REF_KEY.inp_email}
                type="email"
                autoComplete="on"
                placeholder={t(s_signin.enter_your_email)}
                style={{ height: '52px' }}
                {...register('email', {
                  required: { value: true, message: t(s_signin.enter_your_email) },
                  pattern: {
                    value: emailRegExpServer,
                    message: t(s_signin.enter_valid_email),
                  },
                })}
              />
              {errors.email?.message && (
                <div className="text-12 px-[0px] pt-[2px] text-left text-red-500">{errors.email?.message}</div>
              )}
            </div>

            {/* 비밀번호 입력란 */}
            <div className="mb-[12px]">
              <Input
                tabIndex={2}
                dataRef={DATA_REF_KEY.inp_password}
                type="password"
                autoComplete="on"
                placeholder={t(s_signin.enter_your_password)}
                style={{ height: '52px' }}
                {...register('password', {
                  required: { value: true, message: t(s_signin.enter_your_password) },
                  minLength: { value: 6, message: t(s_signin.password_length) },
                  pattern: { value: noWhitespaceRegExp, message: t(s_signin.password_space) },
                })}
              />
              {errors.password?.message && (
                <div className="text-12 px-[0px] pt-[2px] text-left text-red-500">{errors.password?.message}</div>
              )}
            </div>

            {/* 로그인 버튼 */}
            <Button
              data-ref={DATA_REF_KEY.btn_signin}
              tabIndex={3}
              type="submit"
              primary
              lg
              full
              disabled={!isValid || isSubmitting}
              loading={isLoginLoading}
            >
              {t(s_signin.sign_in)}
            </Button>
          </form>

          {/* 비밀번호를 잊으셨나요 */}
          <div className="text-14 mt-[16px] text-gray-500">
            <LinkLocale href={urls.student.auth.passwordReset} clx="ml-auto form-text small text-muted">
              {t(s_signin.forgot_password)}
            </LinkLocale>
          </div>

          {/* 소셜 로그인 */}
          <>
            {/* 소셜 계정으로 로그인 */}
            <div className="text-h6 sm:text-h5 mt-[84px] mb-[20px] font-medium text-gray-700">
              {t(s_signin.sign_in_others)}
            </div>

            <div className="flex flex-row items-center justify-center">
              {/* 카카오 로그인 */}
              {isInApp ? (
                // 인앱일 떄
                <SignUpLogoNew dataRef={DATA_REF_KEY.btn_kakao} type="kakao" clx={space} onClick={handleKakaoInapp} />
              ) : (
                // 인앱 아닐 떄
                <KakaoLogin
                  token={KAKAO_KEY}
                  onSuccess={(result) => handleKakaoLogin(result)}
                  onFail={(result) => console.log(result)}
                  render={(props: any) => <SignUpLogoNew clx={space} type="kakao" onClick={props.onClick} />}
                  // getProfile={true}
                />
              )}

              {/* 네이버 로그인 */}
              {loadNaver ? (
                <NaverLogin
                  clientId={NAVER_KEY}
                  callbackUrl={isInApp ? `https://${RINGLE_DOMAIN}/login?inapp=true` : `https://${RINGLE_DOMAIN}/login`}
                  render={(props) => (
                    <SignUpLogoNew dataRef={DATA_REF_KEY.btn_naver} clx={space} type="naver" onClick={props.onClick} />
                  )}
                  onSuccess={(naverUser) => console.log(naverUser)}
                  onFailure={(result) => console.error(result)}
                />
              ) : (
                <SignUpLogoNew clx={space} type="naver" onClick={() => setLoadNaver(true)} />
              )}

              {/* 구글 로그인 */}
              {isInApp || (
                <GoogleLogin
                  locale={locale}
                  isMobile={isMobile}
                  render={<SignUpLogoNew dataRef={DATA_REF_KEY.btn_google} clx={space} type="google" />}
                />
              )}

              {/* 페이스북 로그인 */}
              {isInApp ? (
                // 인앱일 떄 (페이스북 로그인 for mobile browsers -  SDK 아닌 redirection 방식)
                <a
                  href={`https://www.facebook.com/v14.0/dialog/oauth?client_id=${FACEBOOK_APP_ID}&scope=email&response_type=token&redirect_uri=https://${RINGLE_DOMAIN}/${locale}${urls.student.auth.signUpSocialCallback}?type=facebook`}
                >
                  <SignUpLogoNew clx={'mx-4'} type="facebook" size={'sm'} />
                </a>
              ) : (
                // 인앱 아닐 떄
                <FacebookLoginBtn />
              )}

              {/* 애플 로그인 */}
              <AppleSignin
                /** Auth options passed to AppleID.auth.init() */
                authOptions={{
                  /** Client ID - eg: 'com.example.com' */
                  clientId: 'com.ringleplus',
                  /** Requested scopes, seperated by spaces - eg: 'email name' */
                  scope: 'email',
                  /** Apple's redirectURI - must be one of the URIs you added to the serviceID - the undocumented trick in apple docs is that you should call auth from a page that is listed as a redirectURI, localhost fails */
                  redirectURI: 'https://www.ringleplus.com',
                  /** State string that is returned with the apple response */
                  state: 'state',
                  /** Nonce */
                  nonce: 'nonce',
                  /** Uses popup auth instead of redirection */
                  usePopup: true,
                }} // REQUIRED
                /** General props */
                uiType="dark"
                /** className */
                className="apple-auth-btn"
                /** Removes default style tag */
                noDefaultStyle={false}
                /** Allows to change the button's children, eg: for changing the button text */
                buttonExtraChildren="Continue with Apple"
                /** Extra controlling props */
                /** Called upon signin success in case authOptions.usePopup = true -- which means auth is handled client side */
                onSuccess={(response) => responseApple(response)} // default = undefined
                /** Called upon signin error */
                onError={(error) => alert('error')} // default = undefined
                /** Skips loading the apple script if true */
                skipScript={false} // default = undefined
                /** Apple image props */
                iconProps={{ style: { marginTop: '10px' } }} // default = undefined
                /** render function - called with all props - can be used to fully customize the UI by rendering your own component  */
                // render={(props) => <SignUpLogo {...props} clx="mx-12" type="apple" />}
                render={(props) => (
                  <SignUpLogoNew dataRef={DATA_REF_KEY.btn_apple} {...props} clx={space} type="apple" />
                )}
              />
            </div>

            {/* 인앱 구글 로그인 불가 안내 */}
            {isInApp && (
              <div className="text-h6 mx-[12px] mt-[20px] rounded-[4px] bg-[#F8F8FB] p-[12px] font-medium">
                {t(s_signin.inapp_alert)}
              </div>
            )}
          </>

          {/* 링글이 처음이신가요? 회원가입 */}
          <div className="text-h5 sm:text-h4 mt-[36px] flex flex-row items-center justify-center font-medium sm:mt-[40px]">
            <div className="text-gray-700">{t(s_signin.no_ringle_account)} &nbsp;</div>
            <LinkLocale href={urls.student.auth.new}>
              <div className="text-purple-500" onClick={() => onClickSignUp()}>
                {t(s_signin.sign_up)}
              </div>
            </LinkLocale>
          </div>
        </div>
      </CenterContainer>
    </div>
  )
}

export default SignInPageNew
